// ClientBookings.js
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DateTime } from "luxon";
import Select from "react-select";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { loadBookingEditDraft } from "../ClientRequests/bookingEditDraft";
import useClientRequestsEnabled from "../ClientRequests/useClientRequestsEnabled";
import usePaymentReturn from "../usePaymentReturn";
import PaymentResultOverlay from "../PaymentResultOverlay";
import "./clientBookings.css";

const ALL_PROPS_OPTION = { value: null, label: "All Properties" };
const VALID_VIEWS = ["ongoing", "future", "past"];

function ClientBookings() {
  const [searchParams, setSearchParams] = useSearchParams();

  const pageFromUrl = Number(searchParams.get("page")) || 1;
  const rawViewFromUrl = searchParams.get("view");
  const urlViewValid = VALID_VIEWS.includes(rawViewFromUrl);
  const propertyFromUrl = searchParams.get("property"); // string or null

  const [currentPage, setCurrentPage] = useState(pageFromUrl);
  // When the URL carries an explicit valid view we honour it immediately.
  // Otherwise the view is `null` until the ongoing peek decides — this prevents
  // rendering the toggle in a wrong-default state that then "snaps" to ongoing.
  const [view, setView] = useState(urlViewValid ? rawViewFromUrl : null);

  const [bookings, setBookings] = useState([]);
  const [properties, setProperties] = useState([ALL_PROPS_OPTION]);

  const [selectedProperty, setSelectedProperty] = useState(ALL_PROPS_OPTION);

  // `isLoading` drives the in-content spinner for every fetch (initial + tab switch).
  // Start it true so the first paint shows a spinner instead of a bare empty state
  // while the peek + first bookings fetch are running.
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [totalPages, setTotalPages] = useState(1);

  // Separate peek count — drives whether to show the Ongoing tab at all.
  const [ongoingCount, setOngoingCount] = useState(0);
  const initialisedRef = useRef(false);

  // LEGACY resume params (?resumeDraft=1 / ?resumeEditDraft=1). The request
  // and edit forms are PAGES now (B1) with their own ?resumeDraft=1 contract,
  // but pre-switch history entries, bfcache restores and the old paypage
  // return copy can still land the params HERE — forward them to the page so
  // no draft is ever stranded on the list. Captured once at mount (the
  // URL-sync effect strips unknown params on its first write).
  const legacyResumeRef = useRef({
    create: searchParams.get("resumeDraft") === "1",
    edit: searchParams.get("resumeEditDraft") === "1",
  });

  const requestsEnabled = useClientRequestsEnabled();

  // Pending booking requests (awaiting payment OR awaiting admin approval) —
  // interleaved INTO the Upcoming list in pure date order (2026-07-17:
  // "together with confirmed and other booking types... in actual order, not
  // appears first"). Only the status chip tells them apart; clicking one opens
  // the proper booking-style request details page.
  const [pendingRequests, setPendingRequests] = useState([]);
  // Bookings that already have a pending change / cancellation request
  const [pendingEditTargets, setPendingEditTargets] = useState(new Set());
  const [pendingCancelTargets, setPendingCancelTargets] = useState(new Set());
  // Captured once, so later URL sync (which itself writes `view`) can't flip it.
  const initialUrlViewValidRef = useRef(urlViewValid);

  const navigate = useNavigate();

  // Forward the LEGACY resume params to the pages (B1) — one replace
  // navigation, before the URL-sync effect strips them. The edit draft's own
  // booking_id names the page to land on; a draft that has meanwhile expired
  // (24h TTL) simply falls through to the plain list.
  useEffect(() => {
    if (legacyResumeRef.current.create) {
      navigate("/client/bookings/request?resumeDraft=1", { replace: true });
      return;
    }
    if (legacyResumeRef.current.edit) {
      const draft = loadBookingEditDraft();
      if (draft?.booking_id != null) {
        navigate(`/client/bookings/${draft.booking_id}/edit-request?resumeDraft=1`, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusClass = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "completed" || s === "confirmed") return "cb-statusChip--green";
    if (s === "cancelled" || s === "rescheduled") return "cb-statusChip--red";
    if (s === "pending" || s === "in_progress" || s === "in process") return "cb-statusChip--orange";
    return "cb-statusChip--grey";
  };

  // ----------------------------
  // Load properties for dropdown
  // ----------------------------
  const fetchProperties = useCallback(async () => {
    try {
      const response = await axios.get("/api/client-requests/properties/");
      const formatted = (response.data?.properties || []).map((property) => ({
        value: property.property_id,
        label: property.unit_number ? `Unit ${property.unit_number} · ${property.building_name}` : property.building_name,
      }));

      // keep All first
      setProperties([ALL_PROPS_OPTION, ...formatted]);
    } catch (err) {
      console.error("Error fetching properties:", err);
      setProperties([ALL_PROPS_OPTION]);
    }
  }, []);

  // Price adjustments RETIRED (2026-07-21) — no adjustments card to seed.

  // ---------------------------------------
  // Sync selectedProperty from URL after props loaded
  // ---------------------------------------
  useEffect(() => {
    if (!properties?.length) return;

    if (propertyFromUrl) {
      const found = properties.find((p) => String(p.value) === String(propertyFromUrl));
      setSelectedProperty(found || ALL_PROPS_OPTION);
    } else {
      setSelectedProperty(ALL_PROPS_OPTION);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyFromUrl, properties.length]);

  // ----------------------------
  // Load bookings
  // ----------------------------
  const fetchBookings = useCallback(async () => {
    // Don't fetch while we're still waiting for the peek to decide the view.
    if (!view || !currentPage) return;

    setIsLoading(true);
    setError("");
    setBookings([]); // ✅ hide old bookings immediately while loading

    try {
      let endpoint = `/api/client-requests/bookings/all/${view}?page=${currentPage}`;

      // Only pass property_id if not All
      if (selectedProperty?.value !== null && selectedProperty?.value !== undefined) {
        endpoint += `&property_id=${selectedProperty.value}`;
      }

      const response = await axios.get(endpoint);
      setBookings(Array.isArray(response.data?.bookings) ? response.data.bookings : []);
      setTotalPages(response.data?.pagination?.totalPages || 1);
    } catch (err) {
      console.error(`Error fetching ${view} bookings:`, err);
      setError("Failed to load bookings. Please try again.");
      setBookings([]);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  }, [view, currentPage, selectedProperty]);

  // ----------------------------
  // Load pending booking requests
  // ----------------------------
  const fetchPendingRequests = useCallback(async () => {
    if (!requestsEnabled) { setPendingRequests([]); setPendingEditTargets(new Set()); setPendingCancelTargets(new Set()); return; }
    try {
      // status=pending keeps the 50-row window from ever pushing out a pending
      // row behind historical approved/rejected ones (pending is capped at 5).
      const res = await axios.get("/api/client-requests/requests/my", { params: { status: "pending" } });
      const rows = Array.isArray(res.data?.requests) ? res.data.requests : [];
      setPendingRequests(rows.filter((r) => r.request_type === "create_booking" && r.status === "pending"));
      // Bookings with an open change/cancellation request get a chip instead
      setPendingEditTargets(new Set(
        rows.filter((r) => r.request_type === "edit_booking" && r.status === "pending")
          .map((r) => r.target_id)
      ));
      setPendingCancelTargets(new Set(
        rows.filter((r) => r.request_type === "cancel_booking" && r.status === "pending")
          .map((r) => r.target_id)
      ));
    } catch (err) {
      if (err.response?.status !== 503) console.error("Error fetching pending booking requests:", err);
      setPendingRequests([]);
      setPendingEditTargets(new Set());
      setPendingCancelTargets(new Set());
    }
  }, [requestsEnabled]);

  // Back from the hosted card paypage (?payment=return&ref=…) — the booking-
  // request checkout redirects here. Verify the order against the gateway
  // (poll while pending); a paid outcome means the backend has created and
  // submitted the booking request, so refresh the pending list. The outcome
  // renders in the full-screen PaymentResultOverlay below.
  const payReturn = usePaymentReturn({ onPaid: () => fetchPendingRequests() });

  // ----------------------------
  // Initial load props
  // ----------------------------
  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    fetchPendingRequests();
  }, [fetchPendingRequests]);

  // ----------------------------
  // Peek ongoing count (drives tab visibility + initial default view)
  // ----------------------------
  const fetchOngoingCount = useCallback(async () => {
    try {
      const res = await axios.get(`/api/client-requests/bookings/all/ongoing?page=1`);
      const total = res.data?.pagination?.totalBookings ?? 0;
      setOngoingCount(total);

      if (!initialisedRef.current) {
        initialisedRef.current = true;
        // If the URL didn't carry a valid view, pick ongoing when there's live
        // activity, otherwise fall back to future. This commits view + ongoingCount
        // together so the toggle renders in one pass with the right highlight.
        if (!initialUrlViewValidRef.current) {
          setView(total > 0 ? "ongoing" : "future");
          setCurrentPage(1);
        }
      }
    } catch (err) {
      console.error("Error fetching ongoing count:", err);
      if (!initialisedRef.current) {
        initialisedRef.current = true;
        // Unblock the page even on error — default to upcoming.
        if (!initialUrlViewValidRef.current) setView("future");
      }
    }
  }, []);

  useEffect(() => {
    fetchOngoingCount();
  }, [fetchOngoingCount]);

  // ----------------------------
  // Update URL when filters change
  // ----------------------------
  useEffect(() => {
    // Don't sync until the peek has chosen a view — otherwise we'd write
    // ?view=null or leak the un-resolved state to the address bar.
    if (!view) return;

    const params = new URLSearchParams();
    params.set("view", view);
    params.set("page", String(currentPage));

    // only include property param if not All
    if (selectedProperty?.value !== null && selectedProperty?.value !== undefined) {
      params.set("property", String(selectedProperty.value));
    }

    setSearchParams(params, { replace: true });
  }, [view, currentPage, selectedProperty, setSearchParams]);

  // ----------------------------
  // Fetch bookings on changes
  // ----------------------------
  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const viewLabel = useMemo(() => {
    if (view === "ongoing") return "Ongoing Bookings";
    if (view === "past") return "Past Bookings";
    if (view === "future") return "Upcoming Bookings";
    return "\u00A0"; // non-breaking space — holds the line height while resolving
  }, [view]);

  const showOngoingTab = ongoingCount > 0 || view === "ongoing";
  const viewReady = view !== null;

  // Pending create_booking requests belong in the Upcoming list, interleaved
  // with real bookings in pure chronological order (no pinned section).
  // Respect the property filter so they line up with the bookings shown.
  const showPending = view === "future" && requestsEnabled;
  const visiblePending = useMemo(() => {
    if (!showPending) return [];
    const propId = selectedProperty?.value;
    return pendingRequests.filter(
      (r) => propId === null || propId === undefined || String(r.payload?.property_id) === String(propId)
    );
  }, [showPending, pendingRequests, selectedProperty]);

  // Merge bookings + pending requests, sorted by start time ascending (the
  // future view is served ASC). Requests carry Dubai wall-clock times,
  // bookings carry UTC ISO strings — comparing epoch millis makes them
  // interleave correctly. With multiple pages, a request only appears on the
  // page whose date range contains it (edges extend on the first/last page)
  // so it can't duplicate across pages or jump ahead of earlier dates.
  const listItems = useMemo(() => {
    const bookingItems = bookings.map((b) => {
      const ts = DateTime.fromISO(b.booking_start, { zone: "utc" }).toMillis();
      return { kind: "booking", key: `bk-${b.booking_id}`, data: b, ts: Number.isFinite(ts) ? ts : Infinity };
    });
    if (!visiblePending.length) return bookingItems;

    const requestItems = visiblePending.map((r) => {
      const ts = DateTime.fromISO(String(r.payload?.booking_start || "").replace(" ", "T"), { zone: "Asia/Dubai" }).toMillis();
      return { kind: "request", key: `req-${r.id}`, data: r, ts: Number.isFinite(ts) ? ts : Infinity };
    });

    const firstTs = bookingItems.length ? bookingItems[0].ts : null;
    const lastTs = bookingItems.length ? bookingItems[bookingItems.length - 1].ts : null;
    const onThisPage = requestItems.filter((it) => {
      if (totalPages <= 1) return true;
      const afterStart = currentPage === 1 || (firstTs != null && it.ts >= firstTs);
      const beforeEnd = currentPage === totalPages || (lastTs != null && it.ts <= lastTs);
      return afterStart && beforeEnd;
    });

    return [...bookingItems, ...onThisPage].sort((a, b) => a.ts - b.ts);
  }, [bookings, visiblePending, currentPage, totalPages]);

  return (
    <div className="cb">
      <div className="cb__header">
        <div className="cb__headerLeft">
          <h1 className="cb__title">My Bookings</h1>
          <div className="cb__sub">{viewLabel}</div>
        </div>

        <div className="cb__propFilter">
          <Select
            classNamePrefix="cb-select"
            options={properties}
            value={selectedProperty}
            onChange={(selected) => {
              setSelectedProperty(selected || ALL_PROPS_OPTION);
              setCurrentPage(1);
            }}
            isSearchable
            placeholder="All Properties"
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
            isDisabled={isLoading}
          />
        </div>

        <div className="cb__filters">
          <div className={`cb__toggle ${viewReady ? "" : "cb__toggle--loading"}`}>
            {!viewReady ? (
              // Reserved-space placeholder — keeps layout stable while the peek
              // decides which tab should be active.
              <span className="cb__togglePlaceholder" aria-hidden="true" />
            ) : (
              <>
            {showOngoingTab && (
              <button
                className={`cb__toggleBtn cb__toggleBtn--ongoing ${view === "ongoing" ? "is-active" : ""}`}
                onClick={() => {
                  setView("ongoing");
                  setCurrentPage(1);
                }}
                type="button"
              >
                <span className="cb__toggleDot" aria-hidden="true" /> Ongoing
              </button>
            )}

            <button
              className={`cb__toggleBtn ${view === "future" ? "is-active" : ""}`}
              onClick={() => {
                setView("future");
                setCurrentPage(1);
              }}
              type="button"
            >
              Upcoming
            </button>

            <button
              className={`cb__toggleBtn ${view === "past" ? "is-active" : ""}`}
              onClick={() => {
                setView("past");
                setCurrentPage(1);
              }}
              type="button"
            >
              Past
            </button>
              </>
            )}
          </div>
        </div>

        {requestsEnabled && (
          <button
            className="client-request-property-btn cb__requestBtn"
            onClick={() => navigate("/client/bookings/request")}
          >
            <FontAwesomeIcon icon={faPlus} /> Request Booking
          </button>
        )}
      </div>

      {/* Card payment outcome (verified against the gateway, never the
          redirect) — full-screen result overlay shared by all return
          surfaces. */}
      <PaymentResultOverlay payReturn={payReturn} surface="bookings" />

      {/* Price adjustments RETIRED (2026-07-21) — a booking's unpaid
          change shows as its pending amount + price-change history on the
          booking itself, no Bookings-page card. */}

      {/* ✅ While loading, show ONLY the spinner (no cards, no pagination) */}
      {isLoading ? (
        <div className="cb__loading">
          <div className="app-loading">
            <div className="app-spinner" />
            <div className="app-spinner-text">Loading bookings…</div>
          </div>
        </div>
      ) : error ? (
        <div className="cb__error">{error}</div>
      ) : bookings.length === 0 && visiblePending.length === 0 ? (
        <div className="client-bookings-empty">
          <div className="client-bookings-empty__icon">
            {view === 'ongoing' ? '⏱️' : '🗓️'}
          </div>
          <div className="client-bookings-empty__title">
            {view === 'ongoing'
              ? 'No ongoing cleanings'
              : view === 'future'
              ? 'No upcoming bookings'
              : 'No past bookings'}
          </div>
          <div className="client-bookings-empty__text">
            {view === 'ongoing'
              ? 'Anything being worked on right now will appear here.'
              : view === 'future'
              ? 'When a booking is scheduled, it will appear here.'
              : "Once bookings are completed, you'll see them listed here."}
          </div>
        </div>
      ) : (
        <>
          <div className="cb__list">
            {listItems.map((item) => {
              // Pending create_booking request — sits in pure date order among
              // the real booking rows but keeps the distinct amber pending-card
              // design (cbCard--pending / cbCard--awaitPay). Opens the
              // booking-style request details page (withdraw / complete
              // payment live in there).
              if (item.kind === "request") {
                const req = item.data;
                const p = req.payload || {};
                const rdt = DateTime.fromISO(String(p.booking_start || "").replace(" ", "T"), { zone: "Asia/Dubai" });
                const rDay = rdt.isValid ? rdt.toFormat("dd") : "—";
                const rMonth = rdt.isValid ? rdt.toFormat("LLL") : "—";
                const rYear = rdt.isValid ? rdt.toFormat("yyyy") : "—";
                const rTime = rdt.isValid ? rdt.toFormat("HH:mm") : "—";
                const awaitingPayment = req.payment_state === "awaiting_payment";
                const openRequest = () => navigate(`/client/bookings/requests/${req.id}`);

                return (
                  <div
                    className={`cbCard cbCard--pending${awaitingPayment ? " cbCard--awaitPay" : ""}`}
                    key={item.key}
                    role="button"
                    tabIndex={0}
                    title="View this request"
                    onClick={openRequest}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") openRequest();
                    }}
                  >
                    {/* LEFT: Date */}
                    <div className="cbCard__date">
                      <div className="cbCard__day">{rDay}</div>
                      <div className="cbCard__month">{rMonth}</div>
                      <div className="cbCard__year">{rYear}</div>
                    </div>

                    {/* MIDDLE: Main info */}
                    <div className="cbCard__main">
                      <div className="cbCard__top">
                        <div className="cbCard__id">Requested Booking</div>
                        <div className="cbCard__prop">{p.property_label || `Property #${p.property_id}`}</div>
                      </div>

                      <div className="cbCard__meta">
                        <div className="cbMeta">
                          <div className="cbMeta__label">Time</div>
                          <div className="cbMeta__value">{rTime}</div>
                        </div>
                        <div className="cbMeta">
                          <div className="cbMeta__label">Duration</div>
                          <div className="cbMeta__value">{p.duration_hours ?? "—"} hrs</div>
                        </div>
                        <div className="cbMeta cbMeta--wide">
                          <div className="cbMeta__label">Type</div>
                          <div className="cbMeta__value">{p.type_label || "—"}</div>
                        </div>
                      </div>

                      {/* Booking notices — sent by the server only while the
                          request is pending, the office-hours line only until
                          the office has opened (routes/services/bookingNotice.js). */}
                      {(req.booking_notice?.last_minute || req.booking_notice?.office_hours_note) && (
                        <div className="cbCard__notice">
                          {req.booking_notice?.last_minute && (
                            <span className="cbCard__noticeChip" title={req.booking_notice.last_minute_note || undefined}>
                              Last-minute
                            </span>
                          )}
                          {req.booking_notice?.office_hours_note && (
                            <span className="cbCard__noticeText">{req.booking_notice.office_hours_note}</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* RIGHT: Status + buttons */}
                    <div className="cbCard__side">
                      {awaitingPayment ? (
                        <span className="cb-statusChip cb-statusChip--awaitPay" title="The card payment for this request was not finished">
                          Awaiting payment
                        </span>
                      ) : (
                        <span className="cb-statusChip cb-statusChip--orange">Pending review</span>
                      )}

                      <button
                        type="button"
                        className="cbBtnGhost cbBtnGhost--tight"
                        onClick={(e) => { e.stopPropagation(); openRequest(); }}
                      >
                        View
                      </button>
                    </div>
                  </div>
                );
              }

              const booking = item.data;
              const dt = DateTime.fromISO(booking.booking_start, { zone: "utc" }).setZone("Asia/Dubai");
              const day = dt.isValid ? dt.toFormat("dd") : "—";
              const month = dt.isValid ? dt.toFormat("LLL") : "—";
              const year = dt.isValid ? dt.toFormat("yyyy") : "—";
              const time = dt.isValid ? dt.toFormat("HH:mm") : "—";

              const hasOpenRequest = pendingEditTargets.has(booking.booking_id)
                || pendingCancelTargets.has(booking.booking_id);
              return (
                <div
                  className={`cbCard${hasOpenRequest ? " cbCard--hasRequest" : ""}`}
                  key={item.key}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/client/bookings/${booking.booking_id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      navigate(`/client/bookings/${booking.booking_id}`);
                    }
                  }}
                  title="Open booking"
                >
                  {/* LEFT: Date */}
                  <div className="cbCard__date">
                    <div className="cbCard__day">{day}</div>
                    <div className="cbCard__month">{month}</div>
                    <div className="cbCard__year">{year}</div>
                  </div>

                  {/* MIDDLE: Main info */}
                  <div className="cbCard__main">
                    <div className="cbCard__top">
                      <div className="cbCard__id">Booking #{booking.booking_id}</div>
                      <div className="cbCard__prop">
                        {booking.unit_number ? `Unit ${booking.unit_number} · ${booking.building_name}` : booking.building_name}
                      </div>
                    </div>

                    <div className="cbCard__meta">
                      <div className="cbMeta">
                        <div className="cbMeta__label">Time</div>
                        <div className="cbMeta__value">{time}</div>
                      </div>

                      <div className="cbMeta">
                        <div className="cbMeta__label">Duration</div>
                        <div className="cbMeta__value">{booking.duration_hours ?? "—"} hrs</div>
                      </div>

                      <div className="cbMeta cbMeta--wide">
                        <div className="cbMeta__label">Type</div>
                        <div className="cbMeta__value">{booking.booking_type || "—"}</div>
                      </div>
                    </div>
                  </div>

                  {/* RIGHT: Status + buttons */}
                  <div className="cbCard__side">
                    <span className={`cb-statusChip ${getStatusClass(booking.booking_status)}`}>
                      {booking.booking_status || "—"}
                    </span>

                    <button
                      type="button"
                      className="cbBtnGhost cbBtnGhost--tight"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/client/bookings/${booking.booking_id}`);
                      }}
                    >
                      View
                    </button>

                    {/* Cancellation chip shows for ANY booking with an open cancel
                        request (pending bookings can be cancelled too); the change
                        chip/button only applies to future confirmed bookings. */}
                    {requestsEnabled && view === "future" && pendingCancelTargets.has(booking.booking_id) ? (
                      <span className="cb-statusChip cb-statusChip--orange" title="Awaiting our team's review">
                        Cancellation requested
                      </span>
                    ) : requestsEnabled && view === "future"
                      && [1, 2, 4].includes(booking.type_id)
                      && String(booking.booking_status || "").toLowerCase() === "confirmed" && (
                      pendingEditTargets.has(booking.booking_id) ? (
                        <span className="cb-statusChip cb-statusChip--orange" title="Awaiting our team's review">
                          Change requested
                        </span>
                      ) : booking.can_client_edit === false ? (
                        /* Server says this booking is not client-editable. Show
                           WHY rather than an Edit button that 403s — and read
                           `=== false` so servers that don't send the flag keep
                           today's behaviour instead of losing Edit everywhere. */
                        <span
                          className="cb-statusChip cb-statusChip--muted"
                          title={booking.client_edit_reason || "This booking can only be changed by our team — please contact us"}
                        >
                          Changes by our team
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="cbBtnGhost cbBtnGhost--tight"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/client/bookings/${booking.booking_id}/edit-request`);
                          }}
                        >
                          Edit Booking
                        </button>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 ? (
            <div className="cb__pagination">
              <button
                className="cbBtnGhost"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
                type="button"
              >
                Previous
              </button>

              <div className="cb__pageInfo">
                Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
              </div>

              <button
                className="cbBtnGhost"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
                type="button"
              >
                Next
              </button>
            </div>
          ) : null}
        </>
      )}

      {/* The Request-Booking and Edit-Booking MODALS that mounted here are
          PAGES now (B1, owner 2026-07-19) — /client/bookings/request and
          /client/bookings/:id/edit-request. The buttons above navigate; the
          cancel flow lives on the edit page and the booking details page. */}
    </div>
  );
}

export default ClientBookings;
